import React, { useState, useEffect } from 'react';
import { Package, AlertTriangle, ExternalLink, Calculator, Settings, BarChart3 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Scope3 = ({ onSubmit, initialData = {} }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    categories: [],
    exclusions: {},
    ...initialData
  });

  // Load initial data when component mounts or initialData changes
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      setFormData(prev => ({
        ...prev,
        ...initialData
      }));
    }
  }, [initialData]);

  // Auto-save to localStorage whenever formData changes
  useEffect(() => {
    if (formData && Object.keys(formData).length > 0) {
      try {
        const currentData = JSON.parse(localStorage.getItem('calculationWorkflowData') || '{}');
        const updatedData = {
          ...currentData,
          scope3: formData
        };
        localStorage.setItem('calculationWorkflowData', JSON.stringify(updatedData));
      } catch (error) {
        console.error('Error saving Scope3 data to localStorage:', error);
      }
    }
  }, [formData]);

  const [errors] = useState({});

  // DMA Categories with sample data
  const sampleCategories = React.useMemo(() => [
    {
      id: 1,
      name: 'Category 4: Upstream Transportation',
      description: 'Transportation and distribution of purchased goods',
      method: 'distance-based',
      enabled: true,
      data: [
        {
          id: 1,
          mode: 'Road - Heavy Truck',
          distance: 5000,
          unit: 'km',
          emissionFactor: 0.089,
          unitEF: 'kg CO2e/km',
          emissions: 445,
          source: 'Data Log Entry #2001'
        },
        {
          id: 2,
          mode: 'Rail',
          distance: 2000,
          unit: 'km',
          emissionFactor: 0.023,
          unitEF: 'kg CO2e/km',
          emissions: 46,
          source: 'Data Log Entry #2002'
        }
      ],
      totalEmissions: 491,
      exclusionRationale: ''
    },
    {
      id: 2,
      name: 'Category 5: Waste Generated in Operations',
      description: 'Treatment and disposal of waste',
      method: 'fuel-based',
      enabled: true,
      data: [
        {
          id: 3,
          wasteType: 'General Waste',
          fuelConsumption: 1200,
          unit: 'L',
          emissionFactor: 2.68,
          unitEF: 'kg CO2e/L',
          emissions: 3216,
          source: 'Data Log Entry #2003'
        },
        {
          id: 4,
          wasteType: 'Recycling',
          fuelConsumption: 800,
          unit: 'L',
          emissionFactor: 2.68,
          unitEF: 'kg CO2e/L',
          emissions: 2144,
          source: 'Data Log Entry #2004'
        }
      ],
      totalEmissions: 5360,
      exclusionRationale: ''
    },
    {
      id: 3,
      name: 'Category 6: Business Travel',
      description: 'Employee travel for business purposes',
      method: 'flight-distance-class',
      enabled: true,
      data: [
        {
          id: 5,
          flightClass: 'Short Haul',
          distance: 500,
          unit: 'km',
          emissionFactor: 0.255,
          unitEF: 'kg CO2e/km',
          emissions: 127.5,
          source: 'Data Log Entry #2005'
        },
        {
          id: 6,
          flightClass: 'Long Haul',
          distance: 8000,
          unit: 'km',
          emissionFactor: 0.139,
          unitEF: 'kg CO2e/km',
          emissions: 1112,
          source: 'Data Log Entry #2006'
        }
      ],
      totalEmissions: 1239.5,
      exclusionRationale: ''
    },
    {
      id: 4,
      name: 'Category 7: Employee Commuting',
      description: 'Employee travel between home and work',
      method: 'distance-based',
      enabled: false,
      data: [],
      totalEmissions: 0,
      exclusionRationale: 'Not material for our organization - less than 1% of total emissions'
    },
    {
      id: 5,
      name: 'Category 9: Downstream Transportation',
      description: 'Transportation and distribution of sold products',
      method: 'tonne-km-based',
      enabled: true,
      data: [
        {
          id: 7,
          mode: 'Road - Light Truck',
          tonneKm: 15000,
          unit: 'tkm',
          emissionFactor: 0.089,
          unitEF: 'kg CO2e/tkm',
          emissions: 1335,
          source: 'Data Log Entry #2007'
        }
      ],
      totalEmissions: 1335,
      exclusionRationale: ''
    }
  ], []);

  // Initialize with sample data if empty
  useEffect(() => {
    if (!formData.categories || formData.categories.length === 0) {
      setFormData(prev => ({
        ...prev,
        categories: sampleCategories
      }));
    }
  }, [formData.categories, sampleCategories]);

  const handleMethodChange = (categoryId, method) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.map(category => 
        category.id === categoryId 
          ? { ...category, method }
          : category
      )
    }));
  };

  const handleCategoryToggle = (categoryId) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.map(category => 
        category.id === categoryId 
          ? { ...category, enabled: !category.enabled }
          : category
      )
    }));
  };

  const handleExclusionRationaleChange = (categoryId, rationale) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.map(category => 
        category.id === categoryId 
          ? { ...category, exclusionRationale: rationale }
          : category
      )
    }));
  };

  const handleDataChange = (categoryId, dataId, field, value) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.map(category => 
        category.id === categoryId 
          ? {
              ...category,
              data: category.data.map(item => 
                item.id === dataId 
                  ? { ...item, [field]: value }
                  : item
              )
            }
          : category
      )
    }));
  };

  const getMethodOptions = (categoryId) => {
    const baseOptions = [
      { value: 'fuel-based', label: t('workflow.calculation.scope3.methods.fuelBased') },
      { value: 'distance-based', label: t('workflow.calculation.scope3.methods.distanceBased') },
      { value: 'spend-based', label: t('workflow.calculation.scope3.methods.spendBased') },
      { value: 'tonne-km-based', label: t('workflow.calculation.scope3.methods.tonneKmBased') }
    ];

    // Add flight distance class for business travel
    if (categoryId === 3) { // Category 6: Business Travel
      baseOptions.push({ value: 'flight-distance-class', label: t('workflow.calculation.scope3.methods.flightDistanceClass') });
    }

    return baseOptions;
  };

  const getInputFields = (method, dataItem) => {
    switch (method) {
      case 'fuel-based':
        return [
          { key: 'wasteType', label: t('workflow.calculation.scope3.inputFields.wasteType'), type: 'text' },
          { key: 'fuelConsumption', label: t('workflow.calculation.scope3.inputFields.fuelConsumption'), type: 'number', unit: 'L' },
          { key: 'emissionFactor', label: t('workflow.calculation.scope3.inputFields.emissionFactor'), type: 'number', unit: 'kg CO2e/L' }
        ];
      case 'distance-based':
        return [
          { key: 'mode', label: t('workflow.calculation.scope3.inputFields.transportMode'), type: 'text' },
          { key: 'distance', label: t('workflow.calculation.scope3.inputFields.distance'), type: 'number', unit: 'km' },
          { key: 'emissionFactor', label: t('workflow.calculation.scope3.inputFields.emissionFactor'), type: 'number', unit: 'kg CO2e/km' }
        ];
      case 'spend-based':
        return [
          { key: 'sector', label: t('workflow.calculation.scope3.inputFields.sector'), type: 'text' },
          { key: 'spend', label: t('workflow.calculation.scope3.inputFields.spend'), type: 'number', unit: 'currency' },
          { key: 'emissionFactor', label: t('workflow.calculation.scope3.inputFields.emissionFactor'), type: 'number', unit: 'kg CO2e/currency' }
        ];
      case 'tonne-km-based':
        return [
          { key: 'mode', label: t('workflow.calculation.scope3.inputFields.transportMode'), type: 'text' },
          { key: 'tonneKm', label: t('workflow.calculation.scope3.inputFields.tonneKm'), type: 'number', unit: 'tkm' },
          { key: 'emissionFactor', label: t('workflow.calculation.scope3.inputFields.emissionFactor'), type: 'number', unit: 'kg CO2e/tkm' }
        ];
      case 'flight-distance-class':
        return [
          { key: 'flightClass', label: t('workflow.calculation.scope3.inputFields.flightClass'), type: 'text' },
          { key: 'distance', label: t('workflow.calculation.scope3.inputFields.distance'), type: 'number', unit: 'km' },
          { key: 'emissionFactor', label: t('workflow.calculation.scope3.inputFields.emissionFactor'), type: 'number', unit: 'kg CO2e/km' }
        ];
      default:
        return [];
    }
  };





  const totalEmissions = formData.categories
    .filter(category => category.enabled)
    .reduce((sum, category) => sum + category.totalEmissions, 0);

  const getErrorClass = (categoryId) => {
    return errors[categoryId] ? 'border-red-500 bg-red-50' : '';
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-8">
      <div className="flex items-center mb-6">
        <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
          <Package className="w-6 h-6 text-green-600" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-800">{t('workflow.calculation.scope3.title')}</h2>
          <p className="text-gray-600">{t('workflow.calculation.scope3.description')}</p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Summary Card */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-green-900">{t('workflow.calculation.scope3.summary.title')}</h3>
              <p className="text-green-700">{t('workflow.calculation.scope3.summary.total', { total: (totalEmissions / 1000).toFixed(2) })}</p>
            </div>
            <div className="text-sm text-green-600">
              {t('workflow.calculation.scope3.summary.categoriesIncluded', { 
                enabled: formData.categories.filter(c => c.enabled).length, 
                total: formData.categories.length 
              })}
            </div>
          </div>
        </div>

        {/* Categories */}
        <div className="space-y-6">
          {formData.categories.map((category) => (
            <div key={category.id} className={`bg-gray-50 rounded-lg p-6 ${getErrorClass(category.id)}`}>
              {/* Category Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={category.enabled}
                      onChange={() => handleCategoryToggle(category.id)}
                      className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500"
                    />
                    <h3 className="text-lg font-semibold text-gray-800">{category.name}</h3>
                  </div>
                  {!category.enabled && (
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">{t('workflow.calculation.scope3.category.excluded')}</span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <Calculator className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-600">
                    {(category.totalEmissions / 1000).toFixed(2)} tCO₂e
                  </span>
                </div>
              </div>

              <p className="text-gray-600 mb-4">{category.description}</p>

              {category.enabled ? (
                <div className="space-y-4">
                  {/* Method Selection */}
                  <div className="bg-white p-4 rounded border">
                    <div className="flex items-center space-x-2 mb-3">
                      <Settings className="w-4 h-4 text-blue-600" />
                      <h4 className="font-medium text-gray-800">{t('workflow.calculation.scope3.category.method')}</h4>
                    </div>
                    <select
                      value={category.method || ''}
                      onChange={(e) => handleMethodChange(category.id, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">{t('workflow.calculation.scope3.category.selectMethod')}</option>
                      {getMethodOptions(category.id).map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Data Table */}
                  {category.method && (
                                      <div className="bg-white p-4 rounded border">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <BarChart3 className="w-4 h-4 text-green-600" />
                        <h4 className="font-medium text-gray-800">{t('workflow.calculation.scope3.category.dataResults')}</h4>
                      </div>
                      <button className="text-blue-600 hover:text-blue-800 text-sm underline flex items-center space-x-1">
                        <ExternalLink className="w-3 h-3" />
                        <span>{t('workflow.calculation.scope3.category.sourceRows')}</span>
                      </button>
                    </div>

                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-gray-300">
                              {getInputFields(category.method).map(field => (
                                <th key={field.key} className="text-left py-2 px-3 font-medium text-gray-700">
                                  {field.label}
                                </th>
                              ))}
                              <th className="text-left py-2 px-3 font-medium text-gray-700">{t('workflow.calculation.scope3.inputFields.emissions')}</th>
                              <th className="text-left py-2 px-3 font-medium text-gray-700">{t('workflow.calculation.scope3.inputFields.source')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {category.data.map((dataItem) => (
                              <tr key={dataItem.id} className="border-b border-gray-200">
                                {getInputFields(category.method).map(field => (
                                  <td key={field.key} className="py-2 px-3">
                                    <input
                                      type={field.type}
                                      value={dataItem[field.key] || ''}
                                      onChange={(e) => handleDataChange(category.id, dataItem.id, field.key, 
                                        field.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value
                                      )}
                                      className={`w-full px-2 py-1 border rounded text-sm ${
                                        errors[`${category.id}_${dataItem.id}_${field.key}`] 
                                          ? 'border-red-500 bg-red-50' 
                                          : 'border-gray-300'
                                      }`}
                                      placeholder={field.unit ? t('workflow.calculation.scope3.inputFields.valuePlaceholder', { unit: field.unit }) : t('workflow.calculation.scope3.inputFields.value')}
                                    />
                                    {errors[`${category.id}_${dataItem.id}_${field.key}`] && (
                                      <p className="text-xs text-red-600 mt-1">
                                        {errors[`${category.id}_${dataItem.id}_${field.key}`]}
                                      </p>
                                    )}
                                  </td>
                                ))}
                                <td className="py-2 px-3 font-semibold text-gray-900">
                                  {dataItem.emissions.toLocaleString()}
                                </td>
                                <td className="py-2 px-3">
                                  <button className="text-blue-600 hover:text-blue-800 text-xs underline">
                                    {dataItem.source}
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {errors[`${category.id}_data`] && (
                        <p className="text-sm text-red-600 mt-2 flex items-center">
                          <AlertTriangle className="w-4 h-4 mr-1" />
                          {errors[`${category.id}_data`]}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                /* Exclusion Rationale */
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="font-medium text-yellow-900 mb-2">{t('workflow.calculation.scope3.category.exclusionRationale')}</h4>
                  <textarea
                    value={category.exclusionRationale}
                    onChange={(e) => handleExclusionRationaleChange(category.id, e.target.value)}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 ${
                      errors[`${category.id}_rationale`] ? 'border-red-500' : 'border-yellow-300'
                    }`}
                    placeholder={t('workflow.calculation.scope3.category.exclusionPlaceholder')}
                    rows={3}
                  />
                  {errors[`${category.id}_rationale`] && (
                    <p className="text-sm text-red-600 mt-1 flex items-center">
                      <AlertTriangle className="w-4 h-4 mr-1" />
                      {errors[`${category.id}_rationale`]}
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Validation Messages */}
        {Object.keys(errors).length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h4 className="text-red-800 font-medium mb-2">Errors</h4>
            <ul className="space-y-1">
              {Object.values(errors).map((error, index) => (
                <li key={index} className="text-red-700 text-sm flex items-center">
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  {error}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Methodology Info */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium text-gray-800 mb-2">{t('workflow.calculation.scope3.methodology.title')}</h4>
          <div className="text-sm text-gray-600 space-y-1">
            <p><strong>Fuel-based:</strong> {t('workflow.calculation.scope3.methodology.fuelBased')}</p>
            <p><strong>Distance-based:</strong> {t('workflow.calculation.scope3.methodology.distanceBased')}</p>
            <p><strong>Spend-based:</strong> {t('workflow.calculation.scope3.methodology.spendBased')}</p>
            <p><strong>Tonne-km-based:</strong> {t('workflow.calculation.scope3.methodology.tonneKmBased')}</p>
            <p><strong>Flight Distance Class:</strong> {t('workflow.calculation.scope3.methodology.flightDistanceClass')}</p>
          </div>
        </div>

        {/* Navigation handled by WorkflowTabView */}
      </div>
    </div>
  );
};

export default Scope3;
